// src/services/userService.ts - COMPLETE MERGED VERSION
import apiClient from '@/api/apiClient';
import { AxiosResponse } from 'axios';
import universalUploadHelper from '@/utils/universalUploadHelper';

// ===== CONSOLIDATED TYPE DEFINITIONS =====

/**
 * Profile interface with comprehensive fields for international platform
 */
export interface Profile {
    id: number;
    documentId: string;

    // Personal Information
    firstName?: string;
    lastName?: string;
    fullName?: string;
    displayName?: string;
    bio?: string;
    avatar?: any;
    coverImage?: any;
    website?: string;
    phoneNumber?: string;
    dateOfBirth?: string;
    birthYear?: string;
    gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
    nationality?: string;
    address?: any;

    // Localization & Preferences
    language: 'vi' | 'en' | 'zh' | 'ja' | 'ko';
    timezone?: string;
    theme?: 'light' | 'dark' | 'auto';
    currency?: 'VND' | 'USD' | 'EUR';

    // Professional Information
    occupation?: string;
    company?: string;
    jobTitle?: string;
    experience?: 'entry_level' | 'mid_level' | 'senior_level' | 'expert';
    specialization?: string;
    location?: string;

    // Social & Verification
    socialLinks?: Record<string, string>;
    verificationLevel: 'unverified' | 'email_verified' | 'phone_verified' | 'document_verified' | 'fully_verified';
    verifiedBadge?: 'none' | 'blue' | 'gold';
    isVerified?: boolean;

    // Account Status & Security
    accountStatus: 'active' | 'suspended' | 'pending_verification' | 'locked' | 'deactivated';
    twoFactorEnabled: boolean;
    marketingConsent: boolean;
    dataRetentionConsent: boolean;

    // Statistics & Activity
    profileCompleteness?: number;
    lastActiveAt?: string;
    loginCount?: number;
    totalSessionTime?: number;
    totalWorks?: number;
    followers?: number;
    following?: number;
    likesReceived?: number;

    // Settings
    privacySettings?: any;
    notificationSettings?: any;

    // Timestamps
    createdAt: string;
    updatedAt: string;
}

/**
 * User information interface
 */
export interface UserInfo {
    id: number;
    documentId?: string;
    username: string;
    email: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    avatar?: string;
    confirmed: boolean;
    blocked: boolean;
    provider: string;
    groups?: any[];
    role?: any;
    profile?: Profile;
    user_profile?: Profile;
    isVerified?: boolean;
    createdAt: string;
    updatedAt: string;
}

/**
 * Profile update parameters
 */
export interface UpdateProfileParams {
    firstName?: string;
    lastName?: string;
    displayName?: string;
    bio?: string;
    website?: string;
    phoneNumber?: string;
    dateOfBirth?: string;
    birthYear?: string;
    gender?: string;
    nationality?: string;
    address?: any;
    language?: string;
    timezone?: string;
    theme?: string;
    currency?: string;
    occupation?: string;
    company?: string;
    jobTitle?: string;
    experience?: string;
    specialization?: string;
    location?: string;
    socialLinks?: Record<string, string>;
    marketingConsent?: boolean;
    dataRetentionConsent?: boolean;
    privacySettings?: any;
    notificationSettings?: any;
}

/**
 * User search parameters
 */
export interface UserSearchParams {
    query?: string;
    username?: string;
    email?: string;
    role?: string;
    group?: string;
    accountStatus?: string;
    verificationLevel?: string;
    hasAvatar?: boolean;
    createdAfter?: string;
    createdBefore?: string;
    page?: number;
    pageSize?: number;
    sort?: string;

    // ✅ NEW: Additional filters for better control
    blocked?: boolean;
    confirmed?: boolean;
    populate?: string[]; // For backward compatibility

    // ✅ NEW: Priority filters
    prioritizeGroups?: string[]; // e.g., ['PRIMARY_USERS']
    excludeGroups?: string[];
}

/**
 * User list response with pagination
 */
export interface UserListResponse {
    data: UserInfo[];
    meta: {
        pagination: {
            page: number;
            pageSize: number;
            pageCount: number;
            total: number;
        };
    };
}

/**
 * Profile statistics
 */
export interface ProfileStats {
    totalWorks: number;
    totalLicenses: number;
    totalRevenue: number;
    monthlyRevenue: number;
    profileViews: number;
    followers: number;
    following: number;
    likesReceived: number;
    commentsReceived: number;
    downloadsTotal: number;
    loginCount: number;
    totalSessionTime: number;
    profileCompleteness: number;
    verificationLevel: string;
    accountStatus: string;
    lastActiveAt?: string;
}

// ===== MAIN USER SERVICE CLASS =====

/**
 * Complete User Service for Vietrro Portal
 * Handles user management, profiles, social features, and admin functions
 */
class UserService {
    private baseURL = import.meta.env.VITE_API_URL || 'http://localhost:1337';
    private getToken = () => localStorage.getItem('jwt');

    // ===== USER MANAGEMENT METHODS =====

    /**
 * ✅ WORKING: searchUsers method using the successful populate format
 */
    async searchUsers(params: UserSearchParams = {}): Promise<UserListResponse> {
        try {
            // console.log('🔍 UserService: Starting searchUsers with working format');

            const searchParams = new URLSearchParams();

            // Basic filters
            if (params.query) {
                searchParams.append('filters[$or][0][username][$containsi]', params.query);
                searchParams.append('filters[$or][1][email][$containsi]', params.query);
                searchParams.append('filters[$or][2][user_profile][firstName][$containsi]', params.query);
                searchParams.append('filters[$or][3][user_profile][lastName][$containsi]', params.query);
            }

            if (params.username) searchParams.append('filters[username][$containsi]', params.username);
            if (params.email) searchParams.append('filters[email][$containsi]', params.email);
            if (params.role) searchParams.append('filters[role][name][$eq]', params.role);
            if (params.group) searchParams.append('filters[groups][name][$eq]', params.group);

            // Profile filters
            if (params.accountStatus) {
                searchParams.append('filters[user_profile][accountStatus][$eq]', params.accountStatus);
            }
            if (params.verificationLevel) {
                searchParams.append('filters[user_profile][verificationLevel][$eq]', params.verificationLevel);
            }

            // Important filters
            if (params.blocked !== undefined) searchParams.append('filters[blocked][$eq]', params.blocked.toString());
            if (params.confirmed !== undefined) searchParams.append('filters[confirmed][$eq]', params.confirmed.toString());

            // Date filters
            if (params.createdAfter) searchParams.append('filters[createdAt][$gte]', params.createdAfter);
            if (params.createdBefore) searchParams.append('filters[createdAt][$lte]', params.createdBefore);

            (params?.prioritizeGroups ?? ['PRIMARY_USERS']).forEach((groupCode) => {
                searchParams.append('filters[groups][code][$in]', groupCode);
            });
            
            // Pagination
            const page = params.page || 1;
            const pageSize = params.pageSize || 20;
            searchParams.append('pagination[page]', page.toString());
            searchParams.append('pagination[pageSize]', pageSize.toString());

            // ✅ WORKING POPULATE FORMAT (from successful API call)
            searchParams.append('populate[user_profile][populate][avatar]', 'true');
            searchParams.append('populate[user_profile][populate][coverImage]', 'true');
            searchParams.append('populate[groups]', 'true');
            searchParams.append('populate[role]', 'true');

            // Sorting
            searchParams.append('sort', params.sort || 'createdAt:desc');

            const url = `/api/users?${searchParams.toString()}`;
            // console.log('🔗 UserService: API URL:', url);

            const response = await apiClient.get(url);

            // console.log('📊 UserService: API Response:', {
            //     status: response.status,
            //     dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
            //     hasData: !!response.data?.data,
            //     hasMeta: !!response.data?.meta,
            //     dataLength: response.data?.data?.length || (Array.isArray(response.data) ? response.data.length : 0)
            // });

            // Process response
            let users: UserInfo[] = [];
            let pagination = {
                page: page,
                pageSize: pageSize,
                pageCount: 1,
                total: 0
            };

            if (Array.isArray(response.data)) {
                users = response.data;
                pagination.total = users.length;
                pagination.pageCount = Math.ceil(pagination.total / pageSize);
            } else if (response.data?.data && Array.isArray(response.data.data)) {
                users = response.data.data;
                if (response.data.meta?.pagination) {
                    pagination = response.data.meta.pagination;
                } else {
                    pagination.total = users.length;
                    pagination.pageCount = Math.ceil(pagination.total / pageSize);
                }
            }

            // ✅ VALIDATE POPULATED DATA
            // if (users.length > 0) {
            //     const sampleUser = users[0];
            //     console.log('✅ Sample user structure:', {
            //         id: sampleUser.id,
            //         username: sampleUser.username,
            //         hasUserProfile: !!sampleUser.user_profile,
            //         hasAvatar: !!sampleUser.user_profile?.avatar,
            //         avatarUrl: sampleUser.user_profile?.avatar?.url || 'None',
            //         hasGroups: !!sampleUser.groups,
            //         groupCount: sampleUser.groups?.length || 0,
            //         primaryUser: sampleUser.groups?.some((g: any) => g.code === 'PRIMARY_USERS') || false,
            //         hasRole: !!sampleUser.role,
            //         roleName: sampleUser.role?.name || 'None'
            //     });
            // }

            return {
                data: users,
                meta: { pagination }
            };

        } catch (error: any) {
            console.error('❌ UserService: Search users failed:', error);
            throw error;
        }
    }

    /**
     * ✅ WORKING: searchPrimaryUsers method using successful populate format
     */
    async searchPrimaryUsers(params: UserSearchParams = {}): Promise<UserListResponse> {
        try {
            // console.log('👥 UserService: Searching PRIMARY_USERS with working format');

            // Step 1: Get PRIMARY_USERS first
            const primaryParams = new URLSearchParams();
            primaryParams.append('filters[groups][code][$eq]', 'PRIMARY_USERS');
            primaryParams.append('filters[blocked][$eq]', 'false');
            primaryParams.append('filters[confirmed][$eq]', 'true');
            primaryParams.append('pagination[pageSize]', '50');

            // ✅ Use the WORKING populate format
            primaryParams.append('populate[user_profile][populate][avatar]', 'true');
            primaryParams.append('populate[user_profile][populate][coverImage]', 'true');
            primaryParams.append('populate[groups]', 'true');
            primaryParams.append('populate[role]', 'true');

            primaryParams.append('sort', 'createdAt:desc');

            const primaryResponse = await apiClient.get(`/api/users?${primaryParams.toString()}`);
            const primaryUsers = primaryResponse.data?.data || primaryResponse.data || [];

            // console.log(`✅ Found ${primaryUsers.length} PRIMARY_USERS`);

            // Step 2: Get other users if needed
            const maxUsers = params.pageSize || 20;
            let allUsers = [...primaryUsers];

            if (primaryUsers.length < maxUsers) {
                const needed = maxUsers - primaryUsers.length;

                const otherParams = new URLSearchParams();
                otherParams.append('filters[groups][code][$ne]', 'PRIMARY_USERS');
                otherParams.append('filters[blocked][$eq]', 'false');
                otherParams.append('filters[confirmed][$eq]', 'true');
                otherParams.append('pagination[pageSize]', needed.toString());

                // ✅ Use the WORKING populate format
                otherParams.append('populate[user_profile][populate][avatar]', 'true');
                otherParams.append('populate[user_profile][populate][coverImage]', 'true');
                otherParams.append('populate[groups]', 'true');
                otherParams.append('populate[role]', 'true');

                otherParams.append('sort', 'createdAt:desc');

                try {
                    const otherResponse = await apiClient.get(`/api/users?${otherParams.toString()}`);
                    const otherUsers = otherResponse.data?.data || otherResponse.data || [];

                    allUsers = [...primaryUsers, ...otherUsers].slice(0, maxUsers);
                    // console.log(`✅ Combined: ${primaryUsers.length} PRIMARY + ${otherUsers.length} others = ${allUsers.length} total`);
                } catch (error) {
                    console.warn('⚠️ Could not fetch other users, using only PRIMARY_USERS');
                    allUsers = primaryUsers.slice(0, maxUsers);
                }
            }

            // ✅ VALIDATE RESULTS
            // const primaryUsersCount = allUsers.filter(u =>
            //     u.groups?.some((g: any) => g.code === 'PRIMARY_USERS')
            // ).length;

            // console.log(`📊 Final result: ${allUsers.length} total users, ${primaryUsersCount} are PRIMARY_USERS`);

            return {
                data: allUsers,
                meta: {
                    pagination: {
                        page: params.page || 1,
                        pageSize: params.pageSize || 20,
                        pageCount: 1,
                        total: allUsers.length
                    }
                }
            };

        } catch (error: any) {
            console.error('❌ UserService: Search PRIMARY_USERS failed:', error);
            throw error;
        }
    }

    /**
     * Get user by ID with full population
     */
    async getUserById(userId: string): Promise<UserInfo> {
        try {
            const response = await apiClient.get('/api/users', {
                params: {
                    filters: { documentId: {$eq: userId}},
                    // fields: ['username', 'email', 'blocked', 'confirmed', 'createdAt'],
                    populate: {
                        user_profile: {
                            fields: ['accountStatus', 'address', 'bio', 'displayName', 'firstName', 'lastName', 'fullName', 'gender', 'website'],
                            populate: ['avatar', 'coverImage']
                        },
                        company_profile: {
                            fields: ['companyName', 'numberOfEmployees']
                        },
                        groups: true,
                        role: true
                    }
                }
            });

            return response?.data?.[0] || null;
        } catch (error) {
            console.error('❌ UserService: Get user by ID failed:', error);
            throw error;
        }
    }

    // ===== PROFILE MANAGEMENT METHODS =====

    /**
     * Get current user's profile
     */
    async getCurrentUserProfile(): Promise<Profile> {
        try {
            // console.log('🔍 UserService: Fetching current user profile');

            const response = await apiClient.get('/api/user-profiles/me', {
                params: {
                    populate: ['avatar', 'coverImage']
                }
            });

            return response.data.data;
        } catch (error) {
            console.error('❌ UserService: Get current profile failed:', error);
            throw error;
        }
    }

    /**
     * Get other user's profile by ID
     */
    async getUserProfile(userId: string): Promise<Profile> {
        try {
            // console.log(`🔍 UserService: Fetching profile for user ${userId}`);

            const response = await apiClient.get(`/api/user-profiles`, {
                params: {
                    filters: { user: userId },
                    populate: ['avatar', 'coverImage']
                }
            });

            if (response.data.data && response.data.data.length > 0) {
                return response.data.data[0];
            }

            throw new Error('Profile not found');
        } catch (error) {
            console.error('❌ UserService: Get user profile failed:', error);
            throw error;
        }
    }

    /**
     * Update current user's profile
     */
    async updateCurrentUserProfile(profileData: UpdateProfileParams): Promise<Profile> {
        try {
            // console.log('🔄 UserService: Updating current user profile');

            // Get current profile to have documentId
            const currentProfile = await this.getCurrentUserProfile();

            const response = await apiClient.put(`/api/user-profiles/${currentProfile.documentId}`, {
                data: profileData
            });

            // console.log('✅ UserService: Profile updated successfully');
            return response.data.data;
        } catch (error) {
            console.error('❌ UserService: Update profile failed:', error);
            throw error;
        }
    }

    // ===== MEDIA UPLOAD METHODS (ADVANCED) =====

    /**
     * Update avatar using universal upload helper
     */
    async updateAvatar(file: File): Promise<{ avatar: any }> {
        try {
            // console.log('📤 UserService: Uploading avatar');

            const uploadResult = await universalUploadHelper.universalUploadToStrapi(
                this.baseURL,
                this.getToken(),
                [file],
                {
                    schema: 'user-profile',
                    fieldName: 'avatar',
                    customPathStructure: '{schema}/{username}/avatar/{year}/{month}/{day}/{hash}.{ext}'
                }
            );

            if (!uploadResult.success || !uploadResult.files?.length) {
                throw new Error('Upload failed');
            }

            const uploadedFile = uploadResult.files[0];

            // Get current profile
            const currentProfile = await this.getCurrentUserProfile();

            // Update profile with new avatar
            await apiClient.put(`/api/user-profiles/${currentProfile.documentId}`, {
                data: { avatar: uploadedFile.id }
            });

            // console.log('✅ UserService: Avatar updated successfully');
            return {
                avatar: {
                    id: uploadedFile.id,
                    url: uploadedFile.url,
                    name: uploadedFile.name,
                    size: uploadedFile.size,
                    mime: uploadedFile.mime
                }
            };
        } catch (error) {
            console.error('❌ UserService: Update avatar failed:', error);
            throw error;
        }
    }

    /**
     * Update cover image using universal upload helper
     */
    async updateCoverImage(file: File): Promise<{ cover: any }> {
        try {
            // console.log('📤 UserService: Uploading cover image');

            const uploadResult = await universalUploadHelper.universalUploadToStrapi(
                this.baseURL,
                this.getToken(),
                [file],
                {
                    schema: 'user-profile',
                    fieldName: 'coverImage',
                    customPathStructure: '{schema}/{username}/cover/{year}/{month}/{day}/{hash}.{ext}'
                }
            );

            if (!uploadResult.success || !uploadResult.files?.length) {
                throw new Error('Upload failed');
            }

            const uploadedFile = uploadResult.files[0];

            // Get current profile
            const currentProfile = await this.getCurrentUserProfile();

            // Update profile with new cover image
            await apiClient.put(`/api/user-profiles/${currentProfile.documentId}`, {
                data: { coverImage: uploadedFile.id }
            });

            // console.log('✅ UserService: Cover image updated successfully');
            return {
                cover: {
                    id: uploadedFile.id,
                    url: uploadedFile.url,
                    name: uploadedFile.name,
                    size: uploadedFile.size,
                    mime: uploadedFile.mime
                }
            };
        } catch (error) {
            console.error('❌ UserService: Update cover image failed:', error);
            throw error;
        }
    }

    // ===== BASIC UPLOAD METHODS (FALLBACK) =====

    /**
     * Basic avatar upload (fallback method)
     */
    async uploadAvatar(file: File): Promise<any> {
        try {
            // console.log('📤 UserService: Basic avatar upload');

            const formData = new FormData();
            formData.append('files', file);

            const uploadResponse = await apiClient.post('/api/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const uploadedFile = uploadResponse.data[0];

            // Update profile with new avatar
            const currentProfile = await this.getCurrentUserProfile();
            await apiClient.put(`/api/user-profiles/${currentProfile.documentId}`, {
                data: { avatar: uploadedFile.id }
            });

            return uploadedFile;
        } catch (error) {
            console.error('❌ UserService: Basic avatar upload failed:', error);
            throw error;
        }
    }

    /**
     * Basic cover image upload (fallback method)
     */
    async uploadCoverImage(file: File): Promise<any> {
        try {
            // console.log('📤 UserService: Basic cover image upload');

            const formData = new FormData();
            formData.append('files', file);

            const uploadResponse = await apiClient.post('/api/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const uploadedFile = uploadResponse.data[0];

            // Update profile with new cover image
            const currentProfile = await this.getCurrentUserProfile();
            await apiClient.put(`/api/user-profiles/${currentProfile.documentId}`, {
                data: { coverImage: uploadedFile.id }
            });

            return uploadedFile;
        } catch (error) {
            console.error('❌ UserService: Basic cover image upload failed:', error);
            throw error;
        }
    }

    // ===== STATISTICS & ANALYTICS =====

    /**
     * Get profile statistics with fallback mock data
     */
    async getProfileStats(userId?: string): Promise<ProfileStats> {
        try {
            const endpoint = userId ? `/api/user-profiles/${userId}/stats` : '/api/user-profiles/me/stats';
            const response = await apiClient.get(endpoint);

            return response.data.data || this.generateMockStats();
        } catch (error) {
            console.error('❌ UserService: Get profile stats failed, using mock data:', error);
            return this.generateMockStats();
        }
    }

    /**
     * Generate mock statistics for development
     */
    private generateMockStats(): ProfileStats {
        return {
            totalWorks: Math.floor(Math.random() * 50) + 5,
            totalLicenses: Math.floor(Math.random() * 100) + 10,
            totalRevenue: Math.floor(Math.random() * 100000),
            monthlyRevenue: Math.floor(Math.random() * 10000),
            profileViews: Math.floor(Math.random() * 1000) + 100,
            followers: Math.floor(Math.random() * 500) + 50,
            following: Math.floor(Math.random() * 200) + 20,
            likesReceived: Math.floor(Math.random() * 2000) + 100,
            commentsReceived: Math.floor(Math.random() * 500) + 50,
            downloadsTotal: Math.floor(Math.random() * 5000) + 200,
            loginCount: Math.floor(Math.random() * 100) + 20,
            totalSessionTime: Math.floor(Math.random() * 100000) + 10000,
            profileCompleteness: Math.floor(Math.random() * 30) + 70,
            verificationLevel: 'verified',
            accountStatus: 'active'
        };
    }

    /**
     * Get user activity feed
     */
    async getUserActivity(userId?: string, page: number = 1, pageSize: number = 20) {
        try {
            const endpoint = userId ? `/api/users/${userId}/activities` : '/api/users/me/activities';
            const response = await apiClient.get(endpoint, {
                params: {
                    pagination: { page, pageSize },
                    sort: 'createdAt:desc'
                }
            });

            return {
                data: response.data.data || [],
                meta: response.data.meta
            };
        } catch (error) {
            console.error('❌ UserService: Get user activity failed:', error);
            return {
                data: [],
                meta: { pagination: { page: 1, pageSize, pageCount: 0, total: 0 } }
            };
        }
    }

    // ===== SOCIAL FEATURES =====

    /**
     * Follow a user
     */
    async followUser(userId: string): Promise<void> {
        try {
            console.log(`👥 UserService: Following user ${userId}`);
            await apiClient.post(`/api/user-profiles/${userId}/follow`);
            console.log('✅ UserService: User followed successfully');
        } catch (error) {
            console.error('❌ UserService: Follow user failed:', error);
            throw error;
        }
    }

    /**
     * Unfollow a user
     */
    async unfollowUser(userId: string): Promise<void> {
        try {
            console.log(`👥 UserService: Unfollowing user ${userId}`);
            await apiClient.delete(`/api/user-profiles/${userId}/follow`);
            console.log('✅ UserService: User unfollowed successfully');
        } catch (error) {
            console.error('❌ UserService: Unfollow user failed:', error);
            throw error;
        }
    }

    /**
     * Get user's followers
     */
    async getUserFollowers(userId: string, page: number = 1, pageSize: number = 20): Promise<any> {
        try {
            const response = await apiClient.get(`/api/users/${userId}/followers`, {
                params: {
                    pagination: { page, pageSize },
                    populate: ['user_profile', 'user_profile.avatar']
                }
            });

            return {
                data: response.data.data || [],
                meta: response.data.meta
            };
        } catch (error) {
            console.error('❌ UserService: Get user followers failed:', error);
            return {
                data: [],
                meta: { pagination: { page: 1, pageSize, pageCount: 0, total: 0 } }
            };
        }
    }

    /**
     * Get user's following
     */
    async getUserFollowing(userId: string, page: number = 1, pageSize: number = 20): Promise<any> {
        try {
            const response = await apiClient.get(`/api/users/${userId}/following`, {
                params: {
                    pagination: { page, pageSize },
                    populate: ['user_profile', 'user_profile.avatar']
                }
            });

            return {
                data: response.data.data || [],
                meta: response.data.meta
            };
        } catch (error) {
            console.error('❌ UserService: Get user following failed:', error);
            return {
                data: [],
                meta: { pagination: { page: 1, pageSize, pageCount: 0, total: 0 } }
            };
        }
    }

    /**
     * Report profile
     */
    async reportProfile(userId: string, reason: string, description: string): Promise<void> {
        try {
            // console.log(`🚨 UserService: Reporting user ${userId}`);
            await apiClient.post(`/api/user-profiles/${userId}/report`, {
                data: { reason, description }
            });
            // console.log('✅ UserService: Profile reported successfully');
        } catch (error) {
            console.error('❌ UserService: Report profile failed:', error);
            throw error;
        }
    }

    /**
     * Get featured profiles
     */
    async getFeaturedProfiles(page: number = 1, pageSize: number = 10) {
        try {
            const response = await apiClient.get('/api/user-profiles/featured', {
                params: {
                    pagination: { page, pageSize }
                }
            });

            return {
                data: response.data.data,
                meta: response.data.meta
            };
        } catch (error) {
            console.error('❌ UserService: Get featured profiles failed:', error);
            throw error;
        }
    }

    // ===== CONTENT & LICENSES =====

    /**
     * Get user's works/content
     */
    async getUserWorks(userId: string, page: number = 1, pageSize: number = 20): Promise<any> {
        try {
            const response = await apiClient.get('/api/contents', {
                params: {
                    filters: { generatedBy: userId },
                    populate: ['category', 'files'],
                    pagination: { page, pageSize },
                    sort: 'createdAt:desc'
                }
            });

            return {
                data: response.data.data || [],
                meta: response.data.meta
            };
        } catch (error) {
            console.error('❌ UserService: Get user works failed:', error);
            return {
                data: [],
                meta: { pagination: { page: 1, pageSize, pageCount: 0, total: 0 } }
            };
        }
    }

    /**
     * Get user's licenses
     */
    async getUserLicenses(userId?: string, page: number = 1, pageSize: number = 20): Promise<any> {
        try {
            const endpoint = userId ? `/api/licenses?filters[licensee][id][$eq]=${userId}` : '/api/users/me/licenses';
            const response = await apiClient.get(endpoint, {
                params: {
                    populate: ['content', 'licensee', 'rightOwner'],
                    pagination: { page, pageSize },
                    sort: 'createdAt:desc'
                }
            });

            return {
                data: response.data.data || [],
                meta: response.data.meta
            };
        } catch (error) {
            console.error('❌ UserService: Get user licenses failed:', error);
            return {
                data: [],
                meta: { pagination: { page: 1, pageSize, pageCount: 0, total: 0 } }
            };
        }
    }

    // ===== SETTINGS MANAGEMENT =====

    /**
     * Update privacy settings
     */
    async updatePrivacySettings(settings: any): Promise<void> {
        try {
            // console.log('🔐 UserService: Updating privacy settings');
            const currentProfile = await this.getCurrentUserProfile();

            await apiClient.put(`/api/user-profiles/${currentProfile.documentId}`, {
                data: { privacySettings: settings }
            });

            // console.log('✅ UserService: Privacy settings updated');
        } catch (error) {
            console.error('❌ UserService: Update privacy settings failed:', error);
            throw error;
        }
    }

    /**
     * Update notification settings
     */
    async updateNotificationSettings(settings: any): Promise<void> {
        try {
            // console.log('🔔 UserService: Updating notification settings');
            const currentProfile = await this.getCurrentUserProfile();

            await apiClient.put(`/api/user-profiles/${currentProfile.documentId}`, {
                data: { notificationSettings: settings }
            });

            // console.log('✅ UserService: Notification settings updated');
        } catch (error) {
            console.error('❌ UserService: Update notification settings failed:', error);
            throw error;
        }
    }

    /**
     * Change password
     */
    async changePassword(currentPassword: string, newPassword: string): Promise<void> {
        try {
            // console.log('🔑 UserService: Changing password');
            await apiClient.post('/api/auth/change-password', {
                currentPassword,
                password: newPassword,
                passwordConfirmation: newPassword
            });
            // console.log('✅ UserService: Password changed successfully');
        } catch (error) {
            console.error('❌ UserService: Change password failed:', error);
            throw error;
        }
    }

    // ===== VERIFICATION & ACCOUNT LIFECYCLE =====

    /**
     * Request account verification
     */
    async requestVerification(verificationData: any): Promise<void> {
        try {
            // console.log('📋 UserService: Requesting verification');
            await apiClient.post('/api/users/me/request-verification', {
                data: verificationData
            });
            // console.log('✅ UserService: Verification requested successfully');
        } catch (error) {
            console.error('❌ UserService: Request verification failed:', error);
            throw error;
        }
    }

    /**
     * Deactivate account
     */
    async deactivateAccount(reason?: string): Promise<void> {
        try {
            // console.log('⏸️ UserService: Deactivating account');
            await apiClient.post('/api/users/me/deactivate', {
                data: { reason }
            });
            // console.log('✅ UserService: Account deactivated successfully');
        } catch (error) {
            console.error('❌ UserService: Deactivate account failed:', error);
            throw error;
        }
    }

    /**
     * Delete account
     */
    async deleteAccount(password: string, reason?: string): Promise<void> {
        try {
            // console.log('🗑️ UserService: Deleting account');
            await apiClient.delete('/api/users/me', {
                data: { password, reason }
            });
            // console.log('✅ UserService: Account deleted successfully');
        } catch (error) {
            console.error('❌ UserService: Delete account failed:', error);
            throw error;
        }
    }

    // ===== ADMIN FUNCTIONS =====

    /**
     * Update user (admin function) - Strapi v5 compatible
     */
    async updateUser(userId: string, userData: Partial<UserInfo>): Promise<UserInfo> {
        try {
            // console.log(`👨‍💼 UserService: Admin updating user ${userId}`);

            // Get user documentId first for Strapi v5
            const userResponse = await apiClient.get(`/api/users/${userId}`);
            const documentId = userResponse.data.data?.documentId || userId;

            const response = await apiClient.put(`/api/users/${documentId}`, {
                data: {
                    username: userData.username,
                    email: userData.email,
                    confirmed: userData.confirmed,
                    blocked: userData.blocked
                }
            });

            // console.log('✅ UserService: User updated by admin');
            return response.data.data;
        } catch (error) {
            console.error('❌ UserService: Admin update user failed:', error);
            throw error;
        }
    }

    /**
     * Change user account status (admin function)
     */
    async changeUserStatus(userId: string, status: 'active' | 'suspended' | 'locked' | 'deactivated'): Promise<void> {
        try {
            // console.log(`👨‍💼 UserService: Admin changing user ${userId} status to ${status}`);

            const profileResponse = await apiClient.get(`/api/user-profiles`, {
                params: { filters: { user: userId } }
            });

            if (profileResponse.data.data && profileResponse.data.data.length > 0) {
                const profile = profileResponse.data.data[0];

                await apiClient.put(`/api/user-profiles/${profile.documentId}`, {
                    data: { accountStatus: status }
                });

                // console.log(`✅ UserService: User status changed to ${status}`);
            } else {
                throw new Error('User profile not found');
            }
        } catch (error) {
            console.error('❌ UserService: Change user status failed:', error);
            throw error;
        }
    }

    /**
     * Block/unblock user (admin only)
     */
    async blockUser(userId: string, blocked: boolean = true): Promise<void> {
        try {
            // console.log(`👨‍💼 UserService: Admin ${blocked ? 'blocking' : 'unblocking'} user ${userId}`);

            await apiClient.put(`/api/users/${userId}`, {
                blocked
            });

            // console.log(`✅ UserService: User ${blocked ? 'blocked' : 'unblocked'} successfully`);
        } catch (error) {
            console.error('❌ UserService: Block user failed:', error);
            throw error;
        }
    }

    /**
     * Verify user (admin only)
     */
    async verifyUser(userId: string, verified: boolean = true): Promise<void> {
        try {
            // console.log(`👨‍💼 UserService: Admin ${verified ? 'verifying' : 'unverifying'} user ${userId}`);

            const userProfile = await this.getUserProfile(userId);

            await apiClient.put(`/api/user-profiles/${userProfile.documentId}`, {
                data: { isVerified: verified }
            });

            // console.log(`✅ UserService: User ${verified ? 'verified' : 'unverified'} successfully`);
        } catch (error) {
            console.error('❌ UserService: Verify user failed:', error);
            throw error;
        }
    }

    /**
     * Get admin user statistics
     */
    async getAdminUserStats(): Promise<any> {
        try {
            // console.log('📊 UserService: Fetching admin user statistics');

            const response = await apiClient.get('/api/admin/users/stats');

            // console.log('✅ UserService: Admin stats fetched successfully');
            return response.data;
        } catch (error) {
            console.error('❌ UserService: Get admin user stats failed:', error);

            // Return mock admin stats for development
            return {
                totalUsers: Math.floor(Math.random() * 10000) + 1000,
                activeUsers: Math.floor(Math.random() * 8000) + 800,
                verifiedUsers: Math.floor(Math.random() * 5000) + 500,
                newUsersThisMonth: Math.floor(Math.random() * 500) + 50,
                blockedUsers: Math.floor(Math.random() * 100) + 10,
                suspendedUsers: Math.floor(Math.random() * 50) + 5,
                usersByCountry: {
                    'Vietnam': Math.floor(Math.random() * 3000) + 300,
                    'United States': Math.floor(Math.random() * 2000) + 200,
                    'Japan': Math.floor(Math.random() * 1500) + 150,
                    'South Korea': Math.floor(Math.random() * 1000) + 100,
                    'China': Math.floor(Math.random() * 800) + 80
                },
                userGrowth: {
                    thisMonth: Math.floor(Math.random() * 15) + 5,
                    lastMonth: Math.floor(Math.random() * 12) + 3,
                    thisYear: Math.floor(Math.random() * 150) + 50
                }
            };
        }
    }

    // ===== UTILITY METHODS =====

    /**
     * Validate profile data before update
     */
    validateProfileData(data: UpdateProfileParams): Record<string, string> {
        const errors: Record<string, string> = {};

        // Email validation
        if (data.website && !/^https?:\/\/.+/.test(data.website)) {
            errors.website = 'Website phải bắt đầu bằng http:// hoặc https://';
        }

        // Phone validation (basic)
        if (data.phoneNumber && !/^[+]?[\d\s\-()]+$/.test(data.phoneNumber)) {
            errors.phoneNumber = 'Số điện thoại không hợp lệ';
        }

        // Date of birth validation
        if (data.dateOfBirth) {
            const birthDate = new Date(data.dateOfBirth);
            const today = new Date();
            const age = today.getFullYear() - birthDate.getFullYear();

            if (age < 13 || age > 120) {
                errors.dateOfBirth = 'Tuổi phải từ 13 đến 120';
            }
        }

        // Bio length validation
        if (data.bio && data.bio.length > 500) {
            errors.bio = 'Tiểu sử không được vượt quá 500 ký tự';
        }

        // Display name validation
        if (data.displayName && (data.displayName.length < 2 || data.displayName.length > 50)) {
            errors.displayName = 'Tên hiển thị phải từ 2-50 ký tự';
        }

        return errors;
    }

    /**
     * Format user display name
     */
    formatUserDisplayName(user: UserInfo | Profile): string {
        if ('displayName' in user && user.displayName) {
            return user.displayName;
        }

        if (user.firstName || user.lastName) {
            return `${user.firstName || ''} ${user.lastName || ''}`.trim();
        }

        if ('username' in user) {
            return user.username;
        }

        return 'Người dùng';
    }

    /**
     * Get user avatar URL with fallback
     */
    getUserAvatarUrl(user: UserInfo | Profile): string {
        const avatar = user.avatar;

        if (typeof avatar === 'string') {
            return avatar.startsWith('http') ? avatar : `${this.baseURL}${avatar}`;
        }

        if (avatar?.url) {
            return avatar.url.startsWith('http') ? avatar.url : `${this.baseURL}${avatar.url}`;
        }

        // Fallback to default avatar
        return '/assets/images/default-avatar.png';
    }

    /**
     * Get user cover image URL with fallback
     */
    getUserCoverUrl(user: UserInfo | Profile): string {
        const cover = user.coverImage;

        if (typeof cover === 'string') {
            return cover.startsWith('http') ? cover : `${this.baseURL}${cover}`;
        }

        if (cover?.url) {
            return cover.url.startsWith('http') ? cover.url : `${this.baseURL}${cover.url}`;
        }

        // Fallback to default cover
        return '/assets/images/default-cover.jpg';
    }

    /**
     * Calculate profile completeness percentage
     */
    calculateProfileCompleteness(profile: Profile): number {
        const fields = [
            'firstName',
            'lastName',
            'bio',
            'avatar',
            'phoneNumber',
            'dateOfBirth',
            'occupation',
            'company',
            'website'
        ];

        let completed = 0;

        fields.forEach(field => {
            if (profile[field as keyof Profile]) {
                completed++;
            }
        });

        return Math.round((completed / fields.length) * 100);
    }

    /**
     * Get verification badge info
     */
    getVerificationBadgeInfo(profile: Profile): { type: string; label: string; color: string } {
        switch (profile.verificationLevel) {
            case 'fully_verified':
                return { type: 'gold', label: 'Được xác minh đầy đủ', color: '#FFD700' };
            case 'document_verified':
                return { type: 'blue', label: 'Được xác minh giấy tờ', color: '#1890FF' };
            case 'phone_verified':
                return { type: 'silver', label: 'Được xác minh số điện thoại', color: '#C0C0C0' };
            case 'email_verified':
                return { type: 'bronze', label: 'Được xác minh email', color: '#CD7F32' };
            default:
                return { type: 'none', label: 'Chưa xác minh', color: '#999999' };
        }
    }

    /**
     * Get account status info
     */
    getAccountStatusInfo(profile: Profile): { label: string; color: string; icon: string } {
        switch (profile.accountStatus) {
            case 'active':
                return { label: 'Đang hoạt động', color: '#52C41A', icon: '✅' };
            case 'suspended':
                return { label: 'Bị tạm ngưng', color: '#FF4D4F', icon: '⏸️' };
            case 'locked':
                return { label: 'Bị khóa', color: '#FF7A45', icon: '🔒' };
            case 'deactivated':
                return { label: 'Đã vô hiệu hóa', color: '#999999', icon: '💤' };
            default:
                return { label: 'Chờ xác minh', color: '#FAAD14', icon: '⏳' };
        }
    }

    // ===== LOCALIZATION HELPERS =====

    /**
     * Get supported languages list
     */
    getSupportedLanguages(): Array<{ code: string; name: string; nativeName: string }> {
        return [
            { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
            { code: 'en', name: 'English', nativeName: 'English' },
            { code: 'zh', name: 'Chinese', nativeName: '中文' },
            { code: 'ja', name: 'Japanese', nativeName: '日本語' },
            { code: 'ko', name: 'Korean', nativeName: '한국어' }
        ];
    }

    /**
     * Get supported currencies list
     */
    getSupportedCurrencies(): Array<{ code: string; name: string; symbol: string }> {
        return [
            { code: 'VND', name: 'Vietnamese Dong', symbol: '₫' },
            { code: 'USD', name: 'US Dollar', symbol: '$' }
        ]
    }

    /**
     * Format currency amount
     */
    formatCurrency(amount: number, currency: string = 'VND'): string {
        const currencies = this.getSupportedCurrencies();
        const currencyInfo = currencies.find(c => c.code === currency);

        if (!currencyInfo) {
            return amount.toString();
        }

        switch (currency) {
            case 'VND':
                return new Intl.NumberFormat('vi-VN', {
                    style: 'currency',
                    currency: 'VND'
                }).format(amount);
            case 'USD':
                return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                }).format(amount);
            case 'EUR':
                return new Intl.NumberFormat('de-DE', {
                    style: 'currency',
                    currency: 'EUR'
                }).format(amount);
            default:
                return `${currencyInfo.symbol}${amount.toLocaleString()}`;
        }
    }
}

// ===== EXPORT SINGLETON INSTANCE =====

/**
 * Singleton UserService instance for Vietrro Portal
 * Provides comprehensive user management, profile handling, social features, and admin functions
 * 
 * Features:
 * - Multi-language support (vi, en, zh, ja, ko)
 * - Advanced file upload with universal helper
 * - Comprehensive profile management
 * - Social features (follow, report, featured profiles)
 * - Admin functions with proper Strapi v5 documentId support
 * - Extensive error handling and logging
 * - Mock data fallbacks for development
 * - Utility methods for UI integration
 */
const userService = new UserService();

export default userService;

// ===== EXPORT SINGLETON INSTANCE =====

/**
 * Singleton UserService instance for Vietrro Portal
 * Provides comprehensive user management, profile handling, social features, and admin functions
 * 
 * Features:
 * - Multi-language support (vi, en, zh, ja, ko)
 * - Advanced file upload with universal helper
 * - Comprehensive profile management
 * - Social features (follow, report, featured profiles)
 * - Admin functions with proper Strapi v5 documentId support
 * - Extensive error handling and logging
 * - Mock data fallbacks for development
 * - Utility methods for UI integration
 */
